import { useState } from 'react';
import { api } from '../lib/api.js';
import { useApp } from '../store/app.js';
import { Icon } from '../components/Icon.js';

type Mode = 'login' | 'signup';
type FamilyChoice = 'none' | 'create' | 'join';

/** §123 — welcome, then one decision. */
export function SignIn() {
  const { providers, boot } = useApp();
  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [familyChoice, setFamilyChoice] = useState<FamilyChoice>('none');
  const [familyName, setFamilyName] = useState('');
  const [familyCode, setFamilyCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const devLogin = async () => {
    setBusy(true); setError(null);
    try {
      await api.auth.devLogin(email.trim());
      await boot();
    } catch {
      setError('לא הצלחנו להיכנס. נסה שוב.');
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      if (mode === 'login') {
        await api.auth.login(email.trim(), password);
      } else {
        const family =
          familyChoice === 'create' ? { mode: 'create' as const, name: familyName.trim() } :
          familyChoice === 'join' ? { mode: 'join' as const, code: familyCode.trim() } :
          undefined;
        await api.auth.signup({ email: email.trim(), password, name: name.trim() || undefined, family });
      }
      await boot();
    } catch (err) {
      setError(mode === 'login' ? 'אימייל או סיסמה שגויים.' : 'ההרשמה נכשלה. ודא שהאימייל תקין והסיסמה באורך 8 תווים לפחות.');
      void err;
    } finally {
      setBusy(false);
    }
  };

  const canSubmit = email.includes('@') && password.length >= (mode === 'signup' ? 8 : 1)
    && (mode === 'login' || familyChoice !== 'create' || familyName.trim().length > 0)
    && (mode === 'login' || familyChoice !== 'join' || familyCode.trim().length >= 4);

  return (
    <main className="screen stack" style={{ maxWidth: 420, paddingTop: '10vh', gap: 'var(--sp-6)' }}>
      <div className="stack" style={{ gap: 'var(--sp-2)' }}>
        <h1 style={{ fontSize: 30, fontWeight: 700, letterSpacing: '-0.02em' }}>הכסף שלי</h1>
        <p className="subtle" style={{ fontSize: 16 }}>
          לא עוד טבלה של הוצאות. מערכת שיודעת להגיד לך כמה באמת פנוי לך היום.
        </p>
      </div>

      <div className="stack">
        {providers.google ? (
          <a className="btn btn--primary btn--block" href={api.auth.googleUrl('/')}>
            <Icon name="check" size={18} /> המשך עם Google
          </a>
        ) : null}

        <div className="card stack">
          <div className="segmented" role="group" aria-label="כניסה או הרשמה">
            <button aria-pressed={mode === 'login'} onClick={() => setMode('login')}>כניסה</button>
            <button aria-pressed={mode === 'signup'} onClick={() => setMode('signup')}>הרשמה</button>
          </div>

          <input
            className="input" type="email" placeholder="you@example.com" autoComplete="email"
            value={email} onChange={(e) => setEmail(e.target.value)}
          />
          {mode === 'signup' && (
            <input
              className="input" placeholder="שם (לא חובה)" autoComplete="name"
              value={name} onChange={(e) => setName(e.target.value)}
            />
          )}
          <input
            className="input" type="password" placeholder="סיסמה" autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            value={password} onChange={(e) => setPassword(e.target.value)}
          />

          {mode === 'signup' && (
            <div className="stack" style={{ gap: 'var(--sp-2)', paddingTop: 4 }}>
              <span className="field__label">משפחה (לא חובה)</span>
              <div className="segmented" role="group" aria-label="שיוך למשפחה">
                <button aria-pressed={familyChoice === 'none'} onClick={() => setFamilyChoice('none')}>ללא</button>
                <button aria-pressed={familyChoice === 'create'} onClick={() => setFamilyChoice('create')}>התחלת משפחה</button>
                <button aria-pressed={familyChoice === 'join'} onClick={() => setFamilyChoice('join')}>הצטרפות עם קוד</button>
              </div>
              {familyChoice === 'create' && (
                <input className="input" placeholder="שם המשפחה" value={familyName} onChange={(e) => setFamilyName(e.target.value)} />
              )}
              {familyChoice === 'join' && (
                <input className="input" placeholder="קוד הזמנה" style={{ direction: 'ltr', textAlign: 'center' }} value={familyCode} onChange={(e) => setFamilyCode(e.target.value.toUpperCase())} />
              )}
              {familyChoice !== 'none' && (
                <p className="label--tiny">
                  שקיפות מלאה: למי שמנהל/ת את המשפחה יש גישת <strong>צפייה בלבד</strong> (לא עריכה) לנתונים
                  הפיננסיים של כל בני המשפחה. זה מוצג לכל בן משפחה במסך הפרופיל שלו — אף אחד לא מקבל גישה סמויה.
                </p>
              )}
            </div>
          )}

          <button className="btn btn--block" onClick={submit} disabled={busy || !canSubmit}>
            {busy ? '…' : mode === 'login' ? 'כניסה' : 'הרשמה'}
          </button>
        </div>

        {providers.devLogin && (
          <div className="card stack">
            <span className="card-title">כניסת פיתוח</span>
            <button className="btn btn--block" onClick={devLogin} disabled={busy || !email.includes('@')}>
              {busy ? 'נכנס…' : 'כניסה מהירה בלי סיסמה'}
            </button>
            <p className="label--tiny">זמין רק בפיתוח. בייצור הכניסה היא דרך Google או סיסמה בלבד.</p>
          </div>
        )}

        {error && <p className="label" style={{ color: 'var(--danger)' }}>{error}</p>}
      </div>
    </main>
  );
}
